import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import {
  Compass, Target, Star, Rocket, Save, ListChecks, Calendar as CalendarIcon,
  Eye, EyeOff, Lock, ChevronLeft, ChevronRight, Zap, Trophy, Circle, CheckCircle2,
} from "lucide-react";

/**
 * GPS — Growth Planning System
 *  · 1-3-5 : 1 grande objetivo + 3 prioridades + 5 estratégias
 *  · 4-1-1 : Objetivo anual → mensal → semanal (compromissos com check)
 *
 * Privacidade: 1 switch (Partilhar com Secretário & Admin). Admin vê sempre.
 * Ano corrente por defeito; navegador de ano incluído.
 */
const MONTH_LABELS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

export default function GPSPanel() {
  const now = new Date();
  const [tab, setTab] = useState("135");
  const [year, setYear] = useState(now.getFullYear());
  const [d135, setD135] = useState(null);
  const [d411, setD411] = useState(null);
  const [saving, setSaving] = useState(false);
  const [share, setShare] = useState(true);

  const load = async (yr) => {
    try {
      const [r135, r411] = await Promise.all([
        api.get(`/gps/135?year=${yr}`),
        api.get(`/gps/411?year=${yr}`),
      ]);
      setD135(r135.data);
      setD411(r411.data);
      setShare(Boolean(r135.data.share_with_coach || r411.data.share_with_coach));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(year); /* eslint-disable-next-line */ }, [year]);

  const togglePrivacy = async () => {
    const next = !share;
    try {
      await api.put("/gps/privacy", { share_gps_with_coach: next }, { params: { year } });
      setShare(next);
      toast.success(next ? "GPS partilhado com Secretário e Admin" : "GPS voltou a privado");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const save135 = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/gps/135?year=${year}`, patch);
      setD135(data);
      // If the big goal (€) changed, backend auto-syncs to /gps/411 → refetch it
      // so the 4-1-1 tab reflects the new annual value + targets immediately.
      if (patch && patch.big_goal_amount_eur !== undefined) {
        try {
          const r411 = await api.get(`/gps/411?year=${year}`);
          setD411(r411.data);
        } catch { /* ignore, page reload will fix it */ }
      }
      toast.success("GPS 1-3-5 guardado");
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  const save411 = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/gps/411?year=${year}`, patch);
      setD411(data);
      toast.success("GPS 4-1-1 guardado");
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  if (!d135 || !d411) {
    return (
      <div className="kw-card p-6" data-testid="gps-loading">
        <div className="kw-overline">A carregar GPS…</div>
      </div>
    );
  }

  return (
    <div className="kw-card p-0 overflow-hidden" data-testid="gps-panel">
      {/* Header */}
      <div className="p-5 border-b border-[#222] flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="kw-overline flex items-center gap-2">
            <Compass className="w-3 h-3 text-[#E50000]" /> KW Growth Planning System
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tight mt-1">
            O Meu <span className="text-[#E50000]">GPS</span>
          </h2>
          <p className="text-xs text-[#A3A3A3] mt-2 max-w-xl leading-snug">
            Bússola pessoal: 1 grande objetivo, 3 prioridades e 5 estratégias.
            Depois quebra o ano em meses e semanas — 4-1-1.
          </p>
        </div>
        {/* Year switcher */}
        <div className="kw-card px-3 py-2 flex items-center gap-2" data-testid="gps-year">
          <button
            onClick={() => setYear(year - 1)}
            className="text-[#A3A3A3] hover:text-white p-1"
            data-testid="gps-year-prev"
            aria-label="Ano anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="font-display font-black text-xl min-w-[60px] text-center">{year}</div>
          <button
            onClick={() => setYear(year + 1)}
            className="text-[#A3A3A3] hover:text-white p-1"
            data-testid="gps-year-next"
            aria-label="Próximo ano"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Privacy toggle */}
      <div className="px-5 py-3 border-b border-[#222] flex items-center gap-3" data-testid="gps-privacy">
        {share ? <Eye className="w-4 h-4 text-[#22C55E]" /> : <Lock className="w-4 h-4 text-[#737373]" />}
        <div className="flex-1 min-w-0 text-xs text-[#A3A3A3] leading-snug">
          {share
            ? "Partilhado: o teu Secretário do clube e o Admin podem ver o teu GPS para te acompanhar."
            : "Privado: só tu vês o teu GPS. O Admin mantém acesso para gestão da plataforma."}
        </div>
        <button
          onClick={togglePrivacy}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border transition-colors ${
            share
              ? "border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/10"
              : "border-[#444] text-[#A3A3A3] hover:border-white hover:text-white"
          }`}
          data-testid="gps-privacy-toggle"
        >
          {share ? <><EyeOff className="w-3 h-3" /> Tornar Privado</> : <><Eye className="w-3 h-3" /> Partilhar</>}
        </button>
      </div>

      {/* Tabs */}
      <div className="px-5 pt-4 flex gap-2 border-b border-[#222]" data-testid="gps-tabs">
        <TabBtn active={tab === "135"} onClick={() => setTab("135")} icon={Target} testid="gps-tab-135">
          1-3-5 Focus
        </TabBtn>
        <TabBtn active={tab === "411"} onClick={() => setTab("411")} icon={CalendarIcon} testid="gps-tab-411">
          4-1-1 Tracker
        </TabBtn>
      </div>

      <div className="p-5">
        {tab === "135" ? (
          <View135 data={d135.data} onSave={save135} saving={saving} />
        ) : (
          <View411 data={d411.data} onSave={save411} saving={saving} />
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-widest font-bold border-b-2 transition-colors -mb-px ${
        active ? "border-[#E50000] text-white" : "border-transparent text-[#737373] hover:text-white"
      }`}
    >
      <Icon className="w-4 h-4" />
      {children}
    </button>
  );
}

/* ------------- 1-3-5 view (financeiro) ------------- */
function View135({ data, onSave, saving }) {
  // Financial inputs
  const [goal, setGoal] = useState(data.big_goal_amount_eur || 0);
  const [ticket, setTicket] = useState(data.average_ticket_eur || 0);
  const [cvm, setCvm] = useState(data.conv_contact_to_meeting || 0);
  const [cml, setCml] = useState(data.conv_meeting_to_listing || 0);
  const [cls_, setCls_] = useState(data.conv_listing_to_sale || 0);
  // 3 priorities × 5 strategies (nested)
  const seedPrios = () => {
    const src = (data.priorities || []).map((p) => {
      if (typeof p === "string") return { name: p || "", strategies: ["", "", "", "", ""] };
      return {
        name: (p?.name || ""),
        strategies: [...(p?.strategies || []), "", "", "", "", ""].slice(0, 5),
      };
    });
    while (src.length < 3) src.push({ name: "", strategies: ["", "", "", "", ""] });
    return src.slice(0, 3);
  };
  const [prios, setPrios] = useState(seedPrios());
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    setGoal(data.big_goal_amount_eur || 0);
    setTicket(data.average_ticket_eur || 0);
    setCvm(data.conv_contact_to_meeting || 0);
    setCml(data.conv_meeting_to_listing || 0);
    setCls_(data.conv_listing_to_sale || 0);
    setPrios(seedPrios());
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [data]);

  const filled =
    (data.big_goal_amount_eur || 0) > 0 ||
    (data.priorities || []).some((p) => (typeof p === "string" ? (p || "").trim() : (p?.name || "").trim()));

  const save = () => onSave({
    big_goal_amount_eur: Number(goal) || 0,
    average_ticket_eur: Number(ticket) || 0,
    conv_contact_to_meeting: Number(cvm) || 0,
    conv_meeting_to_listing: Number(cml) || 0,
    conv_listing_to_sale: Number(cls_) || 0,
    priorities: prios.map((p) => ({ name: p.name, strategies: p.strategies })),
  });

  // Computed plan (client-side quick preview; source of truth is /gps/plan)
  const salesNeeded = ticket > 0 ? goal / ticket : 0;
  const salesWk = salesNeeded / 52;
  const listWk = cls_ > 0 ? salesWk / cls_ : 0;
  const meetWk = cml > 0 ? listWk / cml : 0;
  const contWk = cvm > 0 ? meetWk / cvm : 0;

  const fmt = (n, d = 1) => (Number.isFinite(n) ? n.toFixed(d) : "0");
  const eur = (n) => `${Math.round(Number(n) || 0).toLocaleString("pt-PT")} €`;
  const pct = (n) => `${Math.round((Number(n) || 0) * 100)}%`;

  return (
    <div className="space-y-6" data-testid="gps-135-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">
          Framework KW: <span className="text-white">1 Objetivo Anual (€) → 3 Prioridades → 5 Estratégias por prioridade (15 total)</span>
        </div>
        {editing ? (
          <button
            onClick={() => { save(); setEditing(false); }}
            disabled={saving}
            className="kw-btn-primary !py-2 !px-4 text-xs inline-flex items-center gap-2"
            data-testid="gps-135-save"
          >
            <Save className="w-3.5 h-3.5" /> {saving ? "A guardar…" : "Guardar"}
          </button>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="kw-btn-secondary !py-2 !px-4 text-xs inline-flex items-center gap-2"
            data-testid="gps-135-edit"
          >
            Editar
          </button>
        )}
      </div>

      {/* Big Goal — €  */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E50000]/20 to-transparent pointer-events-none" />
        <div className="relative border-2 border-[#E50000] p-5" data-testid="gps-135-big">
          <div className="flex items-center gap-2 mb-2">
            <Rocket className="w-4 h-4 text-[#E50000]" />
            <div className="kw-overline text-[#E50000]">1 · Grande Objetivo do Ano · Faturação em €</div>
          </div>
          {editing ? (
            <div className="flex items-baseline gap-3">
              <input
                type="number" min="0" step="1000"
                value={goal || ""}
                onChange={(e) => setGoal(Number(e.target.value) || 0)}
                placeholder="500000"
                className="kw-input font-display text-2xl font-black flex-1"
                data-testid="gps-135-goal-input"
              />
              <span className="font-display text-2xl font-black text-[#E50000]">€</span>
            </div>
          ) : (
            <div className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight" data-testid="gps-135-goal-value">
              {goal > 0 ? eur(goal) : <span className="text-[#737373] italic normal-case font-normal text-base">Define o valor em € — carrega em Editar</span>}
            </div>
          )}
        </div>
      </div>

      {/* Ticket + Conversion inputs — motor de cálculo */}
      <div className="border border-[#333] p-4" data-testid="gps-135-motor">
        <div className="kw-overline mb-3 flex items-center gap-2">
          <Zap className="w-3 h-3 text-[#3B82F6]" /> Motor de cálculo · Ticket médio & Conversões
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <MotorField
            label="Ticket médio (€)" value={ticket} editing={editing}
            onChange={setTicket} placeholder="15000" step={500} min={0} testid="gps-135-ticket"
            display={ticket > 0 ? eur(ticket) : "—"}
          />
          <MotorField
            label="Contacto → Reunião"
            value={cvm} editing={editing} isRate
            onChange={setCvm} placeholder="0.20" step={0.01} min={0} max={1}
            testid="gps-135-cvm" display={cvm > 0 ? pct(cvm) : "—"}
            hint={editing ? "0.20 = 20%" : ""}
          />
          <MotorField
            label="Reunião → Angariação"
            value={cml} editing={editing} isRate
            onChange={setCml} placeholder="0.40" step={0.01} min={0} max={1}
            testid="gps-135-cml" display={cml > 0 ? pct(cml) : "—"}
            hint={editing ? "0.40 = 40%" : ""}
          />
          <MotorField
            label="Angariação → Venda"
            value={cls_} editing={editing} isRate
            onChange={setCls_} placeholder="0.50" step={0.01} min={0} max={1}
            testid="gps-135-cls" display={cls_ > 0 ? pct(cls_) : "—"}
            hint={editing ? "0.50 = 50%" : ""}
          />
        </div>
      </div>

      {/* Weekly plan preview */}
      {ticket > 0 && goal > 0 && cvm > 0 && cml > 0 && cls_ > 0 && (
        <div className="border-l-4 border-[#22C55E] bg-[#22C55E]/5 p-4" data-testid="gps-135-plan">
          <div className="kw-overline text-[#22C55E] mb-2">Plano semanal · calculado automaticamente</div>
          <p className="text-sm text-[#E5E5E5] leading-relaxed">
            Para faturares <strong className="text-white">{eur(goal)}</strong> a um ticket médio de <strong className="text-white">{eur(ticket)}</strong>,
            precisas de fechar <strong className="text-[#22C55E]">{fmt(salesNeeded, 0)} vendas/ano</strong>.
            Com as tuas conversões, isto significa por <strong className="text-white">semana</strong>:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <PlanCell label="Contactos" value={fmt(contWk, 1)} sub="/ semana" />
            <PlanCell label="Reuniões" value={fmt(meetWk, 2)} sub="/ semana" />
            <PlanCell label="Angariações" value={fmt(listWk, 2)} sub="/ semana" />
            <PlanCell label="Vendas" value={fmt(salesWk, 2)} sub="/ semana" />
          </div>
        </div>
      )}

      {/* 3 Priorities × 5 Strategies each */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-[#EAB308]" />
          <div className="kw-overline text-[#EAB308]">3 · Prioridades × 5 Estratégias cada</div>
        </div>
        <div className="space-y-3" data-testid="gps-135-priorities">
          {prios.map((p, i) => (
            <div key={i} className="border border-[#333] p-4" data-testid={`gps-135-prio-${i}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="text-[10px] uppercase tracking-widest text-[#EAB308] font-bold">
                  Prioridade {i + 1}
                </div>
                {editing ? (
                  <input
                    value={p.name}
                    onChange={(e) => {
                      const nx = [...prios]; nx[i] = { ...nx[i], name: e.target.value }; setPrios(nx);
                    }}
                    maxLength={200}
                    placeholder={`Nome da Prioridade ${i + 1} (ex: Geração de Leads)`}
                    className="kw-input flex-1 !py-2 font-bold"
                    data-testid={`gps-135-prio-${i}-name`}
                  />
                ) : (
                  <div className="text-lg font-display font-black uppercase">
                    {p.name || <span className="text-[#737373] italic font-normal normal-case text-sm">Sem nome</span>}
                  </div>
                )}
              </div>
              <div className="grid md:grid-cols-5 gap-2">
                {p.strategies.map((s, j) => (
                  <div key={j} className="border border-[#222] p-2 hover:border-[#22C55E]/60 transition-colors">
                    <div className="text-[10px] uppercase tracking-widest text-[#22C55E] font-bold mb-1 inline-flex items-center gap-1">
                      <Star className="w-3 h-3" /> {j + 1}
                    </div>
                    {editing ? (
                      <textarea
                        value={s}
                        onChange={(e) => {
                          const nx = [...prios];
                          const strs = [...nx[i].strategies]; strs[j] = e.target.value;
                          nx[i] = { ...nx[i], strategies: strs }; setPrios(nx);
                        }}
                        maxLength={200}
                        placeholder={`Estratégia ${j + 1}`}
                        className="kw-input min-h-[54px] text-xs !p-2"
                        data-testid={`gps-135-prio-${i}-strat-${j}`}
                      />
                    ) : (
                      <div className="text-xs min-h-[48px] leading-snug">
                        {s || <span className="text-[#555] italic">—</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {!filled && !editing && (
        <div className="text-xs text-[#737373] italic flex items-center gap-2">
          <Trophy className="w-3.5 h-3.5" /> Ainda não definiste o teu 1-3-5. Carrega em Editar para começar.
        </div>
      )}
    </div>
  );
}

function MotorField({ label, value, editing, isRate = false, onChange, placeholder, step, min, max, testid, display, hint }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-1">{label}</div>
      {editing ? (
        <>
          <input
            type="number" min={min} max={max} step={step}
            value={value || ""}
            onChange={(e) => onChange(Number(e.target.value) || 0)}
            placeholder={placeholder}
            className="kw-input !py-2"
            data-testid={testid}
          />
          {hint && <div className="text-[9px] text-[#666] mt-1">{hint}</div>}
          {isRate && <div className="text-[9px] text-[#555] mt-0.5">Também aceita valores 0-100 (ex: 20).</div>}
        </>
      ) : (
        <div className="font-display text-2xl font-black text-white" data-testid={`${testid}-display`}>{display}</div>
      )}
    </div>
  );
}

function PlanCell({ label, value, sub }) {
  return (
    <div className="text-center border border-[#22C55E]/30 p-3">
      <div className="text-[10px] uppercase tracking-widest text-[#22C55E] font-bold">{label}</div>
      <div className="font-display text-3xl font-black text-white mt-1">{value}</div>
      <div className="text-[10px] text-[#737373] mt-0.5">{sub}</div>
    </div>
  );
}

/* ------------- 4-1-1 view ------------- */
function View411({ data, onSave, saving }) {
  const now = new Date();
  const [months, setMonths] = useState(data.months || []);
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [editingMonth, setEditingMonth] = useState(false);
  // Financial actuals & targets state
  const [actuals, setActuals] = useState(data.monthly_actual_eur || Array(12).fill(0));
  const [targets, setTargets] = useState(data.monthly_targets_eur || Array(12).fill(null));
  const [editingTargets, setEditingTargets] = useState(false);

  useEffect(() => {
    setMonths(data.months || []);
    setActuals([...(data.monthly_actual_eur || []), ...Array(12).fill(0)].slice(0, 12));
    setTargets([...(data.monthly_targets_eur || []), ...Array(12).fill(null)].slice(0, 12));
  }, [data]);

  const annualEur = Number(data.annual_goal_amount_eur) || 0;
  const autoTargetEur = annualEur > 0 ? annualEur / 12 : 0;
  const eur = (n) => `${Math.round(Number(n) || 0).toLocaleString("pt-PT")} €`;

  const resolvedTarget = (i) => {
    const t = targets[i];
    if (t !== null && t !== undefined && t > 0) return t;
    return autoTargetEur;
  };

  const cumActual = (upto) => actuals.slice(0, upto + 1).reduce((a, b) => a + (Number(b) || 0), 0);
  const cumTarget = (upto) => {
    let sum = 0;
    for (let i = 0; i <= upto; i++) sum += resolvedTarget(i);
    return sum;
  };

  const currentMonth = months[selectedMonth] || { monthly_goal: "", weeks: [] };

  const setMonthField = (idx, patch) => {
    const nx = [...months];
    nx[idx] = { ...(nx[idx] || {}), ...patch };
    setMonths(nx);
  };

  const setWeekField = (mIdx, wIdx, patch) => {
    const nx = [...months];
    const m = { ...(nx[mIdx] || {}) };
    const wks = [...(m.weeks || [])];
    wks[wIdx] = { ...(wks[wIdx] || {}), ...patch };
    m.weeks = wks;
    nx[mIdx] = m;
    setMonths(nx);
  };

  const saveMonths = () => onSave({ months });
  const saveTargets = () => onSave({ monthly_targets_eur: targets });
  const saveActual = (idx, value) => {
    const nx = [...actuals]; nx[idx] = Number(value) || 0; setActuals(nx);
    onSave({ monthly_actual_eur: nx });
  };

  // Compute pct for each month (based on non-empty commitments)
  const monthPct = (m) => {
    if (!m?.weeks) return 0;
    let total = 0, done = 0;
    m.weeks.forEach((w) => {
      (w.commitments || []).forEach((c, i) => {
        if ((c || "").trim()) {
          total += 1;
          if ((w.done || [])[i]) done += 1;
        }
      });
    });
    return total > 0 ? Math.round((100 * done) / total) : 0;
  };

  return (
    <div className="space-y-6" data-testid="gps-411-view">
      {/* Annual goal — mirrored from 1-3-5 (readonly here) */}
      <div className="border-2 border-[#3B82F6] p-4 relative" data-testid="gps-411-annual">
        <div className="absolute inset-0 bg-gradient-to-br from-[#3B82F6]/10 to-transparent pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-4 h-4 text-[#3B82F6]" />
            <div className="kw-overline text-[#3B82F6]">Objetivo Anual · Sincronizado com o 1-3-5</div>
          </div>
          <div className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight" data-testid="gps-411-annual-value">
            {annualEur > 0 ? eur(annualEur) : <span className="italic normal-case text-sm text-[#737373]">Define o Grande Objetivo no separador 1-3-5</span>}
          </div>
          {annualEur > 0 && (
            <div className="text-xs text-[#A3A3A3] mt-1">
              Target médio mensal: <strong className="text-white">{eur(autoTargetEur)}</strong>
            </div>
          )}
        </div>
      </div>

      {/* Monthly financial tracker */}
      <div className="border border-[#333] p-4" data-testid="gps-411-financial">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="kw-overline text-[#EAB308] flex items-center gap-2">
            <Trophy className="w-3 h-3" /> Faturação Real vs Target · Atualiza mensalmente
          </div>
          {annualEur > 0 && (
            editingTargets ? (
              <button
                onClick={() => { saveTargets(); setEditingTargets(false); }}
                disabled={saving}
                className="kw-btn-primary !py-1 !px-3 text-[10px] inline-flex items-center gap-1"
                data-testid="gps-411-targets-save"
              >
                <Save className="w-3 h-3" /> Guardar targets
              </button>
            ) : (
              <button
                onClick={() => setEditingTargets(true)}
                className="text-[10px] text-[#A3A3A3] hover:text-white uppercase tracking-widest font-bold"
                data-testid="gps-411-targets-edit"
              >
                Editar targets mensais
              </button>
            )
          )}
        </div>
        {annualEur === 0 ? (
          <div className="text-xs text-[#737373] italic">Define primeiro o Grande Objetivo (€) no 1-3-5 para veres o tracker mensal.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="gps-411-financial-table">
              <thead>
                <tr className="border-b border-[#222]">
                  <th className="text-left text-[10px] uppercase tracking-widest text-[#737373] font-bold py-2 pr-2">Mês</th>
                  <th className="text-right text-[10px] uppercase tracking-widest text-[#737373] font-bold py-2 px-2">Target</th>
                  <th className="text-right text-[10px] uppercase tracking-widest text-[#737373] font-bold py-2 px-2">Real</th>
                  <th className="text-right text-[10px] uppercase tracking-widest text-[#737373] font-bold py-2 px-2">Δ</th>
                  <th className="text-right text-[10px] uppercase tracking-widest text-[#737373] font-bold py-2 pl-2">YTD</th>
                </tr>
              </thead>
              <tbody>
                {MONTH_LABELS.map((label, i) => {
                  const t = resolvedTarget(i);
                  const a = Number(actuals[i]) || 0;
                  const delta = a - t;
                  const isCurrent = now.getMonth() === i && now.getFullYear() === (data._year || now.getFullYear());
                  const isPast = i < now.getMonth();
                  const showYtd = i <= now.getMonth();
                  const cumT = cumTarget(i);
                  const cumA = cumActual(i);
                  return (
                    <tr key={i} className={`border-b border-[#1A1A1A] ${isCurrent ? "bg-[#E50000]/5" : ""}`} data-testid={`gps-411-financial-row-${i}`}>
                      <td className="py-2 pr-2 font-bold uppercase text-xs">
                        {label.slice(0, 3)}
                        {isCurrent && <span className="ml-1 text-[8px] text-[#E50000]">HOJE</span>}
                      </td>
                      <td className="py-2 px-2 text-right font-mono text-xs">
                        {editingTargets ? (
                          <input
                            type="number" min="0" step="500"
                            value={targets[i] || ""}
                            onChange={(e) => {
                              const nx = [...targets];
                              nx[i] = e.target.value === "" ? null : Number(e.target.value);
                              setTargets(nx);
                            }}
                            placeholder={Math.round(autoTargetEur).toString()}
                            className="kw-input !py-1 !px-2 text-right text-xs w-28"
                            data-testid={`gps-411-target-input-${i}`}
                          />
                        ) : (
                          <span className={targets[i] && targets[i] > 0 ? "text-[#EAB308]" : "text-[#737373]"}>{eur(t)}</span>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right font-mono text-xs">
                        {isPast || isCurrent ? (
                          <input
                            type="number" min="0" step="100"
                            value={actuals[i] || ""}
                            onChange={(e) => {
                              const nx = [...actuals]; nx[i] = Number(e.target.value) || 0; setActuals(nx);
                            }}
                            onBlur={(e) => saveActual(i, e.target.value)}
                            placeholder="0"
                            className="kw-input !py-1 !px-2 text-right text-xs w-28 font-bold"
                            data-testid={`gps-411-actual-input-${i}`}
                          />
                        ) : (
                          <span className="text-[#555]">—</span>
                        )}
                      </td>
                      <td className={`py-2 px-2 text-right font-mono text-xs font-bold ${(isPast || isCurrent) && a > 0 ? (delta >= 0 ? "text-[#22C55E]" : "text-[#E50000]") : "text-[#555]"}`}>
                        {(isPast || isCurrent) && (a > 0 || t > 0) ? (delta >= 0 ? "+" : "") + eur(delta) : "—"}
                      </td>
                      <td className="py-2 pl-2 text-right font-mono text-xs">
                        {showYtd ? (
                          <span className={cumA >= cumT ? "text-[#22C55E] font-bold" : "text-[#A3A3A3]"}>
                            {eur(cumA)} <span className="text-[#666]">/ {eur(cumT)}</span>
                          </span>
                        ) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="kw-overline text-[#A3A3A3]">Compromissos Semanais · Foco Operacional</div>

      {/* Month grid */}
      <div>
        <div className="kw-overline mb-3">Meses</div>
        <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-1.5" data-testid="gps-411-months">
          {MONTH_LABELS.map((label, i) => {
            const pct = monthPct(months[i]);
            const isActive = selectedMonth === i;
            const isCurrent = now.getMonth() === i;
            return (
              <button
                key={i}
                onClick={() => { setSelectedMonth(i); setEditingMonth(false); }}
                data-testid={`gps-411-month-${i}`}
                className={`p-2 border text-left transition-colors ${
                  isActive
                    ? "border-[#E50000] bg-[#E50000]/10"
                    : "border-[#222] hover:border-[#444]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest font-bold">{label.slice(0, 3)}</div>
                  {isCurrent && <div className="w-1.5 h-1.5 rounded-full bg-[#E50000]" title="Mês atual" />}
                </div>
                <div className="text-[10px] text-[#737373] mt-0.5">{pct}%</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected month detail */}
      <div className="border border-[#333] p-4" data-testid="gps-411-month-detail">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-[#E50000]" />
            <h3 className="font-display text-lg font-black uppercase">{MONTH_LABELS[selectedMonth]}</h3>
          </div>
          {editingMonth ? (
            <button
              onClick={() => { saveMonths(); setEditingMonth(false); }}
              disabled={saving}
              className="kw-btn-primary !py-2 !px-3 text-xs inline-flex items-center gap-2"
              data-testid="gps-411-month-save"
            >
              <Save className="w-3.5 h-3.5" /> Guardar
            </button>
          ) : (
            <button
              onClick={() => setEditingMonth(true)}
              className="kw-btn-secondary !py-2 !px-3 text-xs"
              data-testid="gps-411-month-edit"
            >
              Editar
            </button>
          )}
        </div>

        {/* Monthly goal */}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-1">Objetivo do mês</div>
          {editingMonth ? (
            <input
              value={currentMonth.monthly_goal || ""}
              onChange={(e) => setMonthField(selectedMonth, { monthly_goal: e.target.value })}
              maxLength={400}
              placeholder={`Objetivo para ${MONTH_LABELS[selectedMonth]}`}
              className="kw-input"
              data-testid="gps-411-month-goal-input"
            />
          ) : (
            <div className="text-sm font-semibold">
              {currentMonth.monthly_goal || <span className="text-[#737373] italic font-normal">—</span>}
            </div>
          )}
        </div>

        {/* Weeks */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="gps-411-weeks">
          {(currentMonth.weeks || []).slice(0, 5).map((week, wIdx) => {
            const commits = week.commitments || [];
            const done = week.done || [];
            const total = commits.filter((c) => (c || "").trim()).length;
            const doneCount = commits.reduce((acc, c, i) => acc + (((c || "").trim() && done[i]) ? 1 : 0), 0);
            return (
              <div key={wIdx} className="border border-[#222] p-3" data-testid={`gps-411-week-${wIdx}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-[#EAB308] font-bold">Semana {wIdx + 1}</div>
                  <div className="text-[10px] text-[#737373]">{doneCount}/{total || 0}</div>
                </div>
                <div className="space-y-1.5">
                  {commits.slice(0, 6).map((c, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <button
                        onClick={() => {
                          if (!(c || "").trim()) return;
                          const nx = [...done];
                          nx[i] = !nx[i];
                          setWeekField(selectedMonth, wIdx, { done: nx });
                          // auto-save the toggle
                          const monthsNext = [...months];
                          const m = { ...(monthsNext[selectedMonth] || {}) };
                          const wks = [...(m.weeks || [])];
                          const wcur = { ...(wks[wIdx] || {}) };
                          wcur.done = nx;
                          wks[wIdx] = wcur;
                          m.weeks = wks;
                          monthsNext[selectedMonth] = m;
                          onSave({ months: monthsNext });
                        }}
                        className={`mt-0.5 flex-shrink-0 ${!(c || "").trim() ? "opacity-30 cursor-not-allowed" : ""}`}
                        data-testid={`gps-411-week-${wIdx}-check-${i}`}
                        title={(c || "").trim() ? "Marcar como concluído" : "Preenche o compromisso primeiro"}
                      >
                        {done[i] ? (
                          <CheckCircle2 className="w-4 h-4 text-[#22C55E]" />
                        ) : (
                          <Circle className="w-4 h-4 text-[#666]" />
                        )}
                      </button>
                      {editingMonth ? (
                        <input
                          value={c}
                          onChange={(e) => {
                            const nx = [...commits]; nx[i] = e.target.value;
                            setWeekField(selectedMonth, wIdx, { commitments: nx });
                          }}
                          maxLength={300}
                          placeholder={`Compromisso ${i + 1}`}
                          className="kw-input !py-1 !px-2 text-xs flex-1"
                          data-testid={`gps-411-week-${wIdx}-input-${i}`}
                        />
                      ) : (
                        <div className={`text-xs flex-1 min-h-[20px] leading-snug ${done[i] ? "line-through text-[#666]" : ""}`}>
                          {c || <span className="text-[#555] italic">—</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
